/**
 * The page's body: the left side, the open place, and what is pinned for it. This file holds What's new
 * and a phone's home; Everyone, Announcements and circles are in Rooms.tsx, Files and Search in
 * FilesSearch.tsx, and the private thread with the host is Conversation.tsx.
 */
import type { ReactNode } from "react";
import { Face, FaceStack } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import type { GuestNewsItem, GuestRoomChannel } from "../../../shared/talk";
import { Conversation } from "./Conversation";
import { FilesPlace, SearchPlace } from "./FilesSearch";
import { Frame } from "./Frame";
import { usePage } from "./context";
import { mmss } from "../room/talk";
import { callWords, dayOf, inWordsOf, joinedWords, liveFor, sinceWords, timeOf } from "./talk";
import { CirclePlace, EveryonePlace, AnnouncePlace } from "./Rooms";
import { faceOf, Head, MeButton, NextLive, TellMe, useOpenFile } from "./Parts";
import { HostAvatar, MadeWith, Side } from "./Side";

export function Places(): ReactNode {
  const { lay } = usePage();
  if (lay === "phone") return <PhoneApp />;
  return (
    <div className="jp-app">
      <Side />
      <Place />
    </div>
  );
}

/** The open place, with its right column. */
function Place(): ReactNode {
  const { place } = usePage();
  switch (place.kind) {
    case "new":
      return <WhatsNew />;
    case "direct":
      return <Conversation />;
    case "files":
      return <FilesPlace />;
    case "search":
      return <SearchPlace q={place.q} />;
    case "everyone":
      return <EveryonePlace id={place.id} />;
    case "announce":
      return <AnnouncePlace id={place.id} />;
    case "circle":
      return <CirclePlace id={place.id} />;
  }
}

function PhoneApp(): ReactNode {
  const { place } = usePage();
  if (place.kind === "new") return <PhoneHome />;
  return <Place />;
}

// --- What's new ---

const roomOf = (rooms: readonly GuestRoomChannel[], id: string) => rooms.find((r) => r.webinarId === id);

function NewsCards({ items }: { items: readonly GuestNewsItem[] }): ReactNode {
  const { guest, host, goPlace, playTalk, nowMs } = usePage();
  const open = useOpenFile();
  const nowSec = Math.floor(nowMs / 1000);
  const many = guest.rooms.length > 1;
  const small = (it: GuestNewsItem) => it.kind === "file" || it.kind === "everyone" || it.kind === "announce";
  const card = (it: GuestNewsItem, i: number): ReactNode => {
    if (it.kind === "answered")
      return (
        <article className="jp-card fresh" key={`a${i}`}>
          <header>
            <Face name={host.name} photo={host.avatar} size={34} />
            <span>
              <b>{host.first} answered you</b>
              <small>
                Only you and {host.first} · {timeOf(it.at)}
              </small>
            </span>
          </header>
          {it.answer ? (
            <p className="jp-q">
              Your answer at {mmss(it.answer.atSec)}: {it.answer.text}
            </p>
          ) : it.asked ? (
            <p className="jp-q">You asked: {it.asked}</p>
          ) : null}
          <p className="jp-a">{it.body}</p>
          <button type="button" className="jp-btn soft s" onClick={() => goPlace({ kind: "direct" })}>
            Reply to {host.first}
          </button>
        </article>
      );
    if (it.kind === "direct")
      return (
        <article className="jp-card fresh" key={`d${i}`}>
          <header>
            <Face name={host.name} photo={host.avatar} size={34} />
            <span>
              <b>{host.first} wrote to you</b>
              <small>
                Only you and {host.first} · {timeOf(it.at)}
              </small>
            </span>
          </header>
          <p className="jp-a">{it.body}</p>
          <button type="button" className="jp-btn soft s" onClick={() => goPlace({ kind: "direct" })}>
            Open {host.first}'s message
          </button>
        </article>
      );
    if (it.kind === "file")
      return (
        <article className="jp-card fresh" key={`f${i}`}>
          <header>
            <span className="jp-ic">
              <Icon name="file" />
            </span>
            <span>
              <b>{it.file.by.kind === "host" ? host.first : it.file.by.name.split(" ")[0]} shared a file</b>
              <small>
                {it.where} · {dayOf(it.at, nowSec)}
              </small>
            </span>
          </header>
          <button type="button" className="jp-file" onClick={() => open(it.file)}>
            <Icon name={it.file.kind === "link" ? "link" : "file"} /> {it.file.name}
          </button>
        </article>
      );
    if (it.kind === "everyone") {
      const room = roomOf(guest.rooms, it.webinarId);
      return (
        <article className="jp-card fresh" key={`e${i}`}>
          <header>
            <FaceStack className="fs" people={it.faces.map(faceOf)} size={26} />
            <span>
              <b>{it.count} new in Everyone</b>
              <small>{many && room ? `${room.title} · ${inWordsOf(it.faces)}` : inWordsOf(it.faces)}</small>
            </span>
          </header>
          <p className="jp-snip">
            <b>{it.latest.who}:</b> {it.latest.text}
          </p>
          <button type="button" className="jp-link" onClick={() => goPlace({ kind: "everyone", id: it.webinarId })}>
            Open Everyone <Icon name="right" />
          </button>
        </article>
      );
    }
    if (it.kind === "announce") {
      const room = roomOf(guest.rooms, it.webinarId);
      return (
        <article className="jp-card fresh" key={`n${i}`}>
          <header>
            <span className="jp-ic">
              <Icon name="bell" />
            </span>
            <span>
              <b>{host.first} posted an announcement</b>
              <small>
                {room?.title ?? "Announcements"} · {dayOf(it.at, nowSec)}
              </small>
            </span>
          </header>
          <p className="jp-snip">{it.post.body}</p>
          <button type="button" className="jp-link" onClick={() => goPlace({ kind: "announce", id: it.webinarId })}>
            Open Announcements <Icon name="right" />
          </button>
        </article>
      );
    }
    return (
      <article className="jp-card row" key={`r${i}`}>
        <span className="jp-thumb">
          {it.posterUrl ? <img src={it.posterUrl} alt="" /> : null}
          <Icon name="playf" />
        </span>
        <span className="jp-card-t">
          <b>Your replay is ready</b>
          <small>
            {it.title} · {callWords(it.startsAt, it.minutes)}
          </small>
        </span>
        <button type="button" className="jp-btn soft s" onClick={() => playTalk(it.webinarId, { start: true })}>
          Watch
        </button>
      </article>
    );
  };
  const out: ReactNode[] = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!small(it)) {
      out.push(card(it, i));
      continue;
    }
    const group: ReactNode[] = [];
    while (i < items.length && small(items[i])) group.push(card(items[i], i++));
    i--;
    out.push(
      <div className="jp-two" key={`two${i}`}>
        {group}
      </div>,
    );
  }
  return out;
}

function WhatsNew(): ReactNode {
  const { guest, host, markAllSeen, nowMs, lay } = usePage();
  const live = guest.liveNow;
  const upright = lay === "upright";
  const right = upright ? null : (
    <>
      <NextLive next={guest.nextLive} />
      <TellMe />
    </>
  );
  return (
    <Frame
      nav={null}
      right={right}
      head={
        <Head
          big
          title="What's new"
          sub={sinceWords(guest.lastHereAt, Math.floor(nowMs / 1000))}
          right={
            <button type="button" className="jp-link" onClick={markAllSeen}>
              <Icon name="check" /> Mark all as seen
            </button>
          }
        />
      }
    >
      <div className="jp-feed">
        {live ? (
          <article className="jp-hot">
            <div className="jp-hot-pic">
              {live.joined.slice(0, 4).map((f, i) => (f.photoUrl ? <img key={`${f.name}${i}`} src={f.photoUrl} alt="" /> : <span key={`${f.name}${i}`} />))}
              <span className="jp-pulse on-pic">
                <i />
                Live · {liveFor(live.startedAt, nowMs)}
              </span>
            </div>
            <div className="jp-hot-t">
              <small className="jp-eb">{live.title}</small>
              <h3>{live.circleId ? `${host.first} is live with your circle` : `${host.first} is live`}</h3>
              {live.joined.length ? (
                <span className="jp-hot-who">
                  <FaceStack className="fs" people={live.joined.map(faceOf)} size={28} />
                  <small>{joinedWords(live.joined.length)}</small>
                </span>
              ) : null}
              <div className="jp-hot-b">
                <a className="jp-btn" href={live.joinPath}>
                  <Icon name="live" /> Join the call
                </a>
              </div>
            </div>
          </article>
        ) : null}
        {guest.news.length ? <NewsCards items={guest.news} /> : <p className="jp-empty">You are all caught up.</p>}
        {upright ? (
          <div className="jp-endcard">
            <NextLive next={guest.nextLive} />
            <TellMe />
          </div>
        ) : null}
      </div>
    </Frame>
  );
}

// --- a phone's home: What's new, then the places ---

function PhoneHome(): ReactNode {
  const { guest, host, goPlace, openRoom, nowMs, playTalk } = usePage();
  const open = useOpenFile();
  const live = guest.liveNow;
  const nowSec = Math.floor(nowMs / 1000);
  return (
    <div className="gq">
      <div className="gq-scroll">
        <header className="gq-top">
          <HostAvatar />
          <h1>{host.name}</h1>
          <MeButton size={36} />
        </header>
        <button type="button" className="jp-q gq-q" onClick={() => goPlace({ kind: "search", q: "" })}>
          <Icon name="search" />
          Search messages and files
        </button>

        {live ? (
          <article className="gq-live">
            <div className="gq-live-pic">
              {live.joined.slice(0, 4).map((f, i) => (f.photoUrl ? <img key={`${f.name}${i}`} src={f.photoUrl} alt="" /> : <span key={`${f.name}${i}`} />))}
              <span className="jp-pulse on-pic">
                <i />
                Live · {liveFor(live.startedAt, nowMs)}
              </span>
            </div>
            <div className="gq-live-t">
              <small className="jp-eb">{live.title}</small>
              <h2>{live.circleId ? `${host.first} is live with your circle` : `${host.first} is live`}</h2>
              {live.joined.length ? (
                <span className="jp-hot-who">
                  <FaceStack className="fs" people={live.joined.map(faceOf)} size={26} />
                  <small>{joinedWords(live.joined.length)}</small>
                </span>
              ) : null}
              <a className="jp-btn light gq-wide" href={live.joinPath}>
                <Icon name="live" /> Join the call
              </a>
            </div>
          </article>
        ) : null}

        {guest.news.length ? (
          <>
            <div className="gq-h">
              <b>What's new</b>
              <PhoneMarkAll />
            </div>
            {guest.news.map((it, i) => {
              if (it.kind === "answered")
                return (
                  <button key={i} type="button" className="jp-card fresh gq-card" onClick={() => goPlace({ kind: "direct" })}>
                    <header>
                      <Face name={host.name} photo={host.avatar} size={32} />
                      <span>
                        <b>{host.first} answered you</b>
                        <small>
                          Only you and {host.first} · {timeOf(it.at)}
                        </small>
                      </span>
                    </header>
                    <p className="jp-a">{it.body}</p>
                  </button>
                );
              if (it.kind === "direct")
                return (
                  <button key={i} type="button" className="jp-card fresh gq-card" onClick={() => goPlace({ kind: "direct" })}>
                    <header>
                      <Face name={host.name} photo={host.avatar} size={32} />
                      <span>
                        <b>{host.first} wrote to you</b>
                        <small>
                          Only you and {host.first} · {timeOf(it.at)}
                        </small>
                      </span>
                    </header>
                    <p className="jp-a">{it.body}</p>
                  </button>
                );
              if (it.kind === "file")
                return (
                  <button key={i} type="button" className="jp-card fresh gq-card" onClick={() => open(it.file)}>
                    <header>
                      <span className="jp-ic">
                        <Icon name="file" />
                      </span>
                      <span>
                        <b>{it.file.by.kind === "host" ? host.first : it.file.by.name.split(" ")[0]} shared a file</b>
                        <small>
                          {it.where} · {dayOf(it.at, nowSec)}
                        </small>
                      </span>
                    </header>
                  </button>
                );
              if (it.kind === "everyone")
                return (
                  <button key={i} type="button" className="jp-card fresh gq-card" onClick={() => goPlace({ kind: "everyone", id: it.webinarId })}>
                    <header>
                      <FaceStack className="fs" people={it.faces.map(faceOf)} size={26} />
                      <span>
                        <b>{it.count} new in Everyone</b>
                        <small>
                          {it.latest.who}: {it.latest.text}
                        </small>
                      </span>
                    </header>
                  </button>
                );
              if (it.kind === "announce")
                return (
                  <button key={i} type="button" className="jp-card fresh gq-card" onClick={() => goPlace({ kind: "announce", id: it.webinarId })}>
                    <header>
                      <span className="jp-ic">
                        <Icon name="bell" />
                      </span>
                      <span>
                        <b>{host.first} posted an announcement</b>
                        <small>{it.post.body}</small>
                      </span>
                    </header>
                  </button>
                );
              return (
                <button key={i} type="button" className="jp-card fresh gq-card" onClick={() => playTalk(it.webinarId, { start: true })}>
                  <header>
                    <span className="jp-ic">
                      <Icon name="playf" />
                    </span>
                    <span>
                      <b>Your replay is ready</b>
                      <small>
                        {it.title} · {callWords(it.startsAt, it.minutes)}
                      </small>
                    </span>
                  </header>
                </button>
              );
            })}
          </>
        ) : null}

        <p className="gq-h2">Your places</p>
        <section className="gq-list">
          <PhonePlace face label={host.first} sub={guest.directLast ?? undefined} end={guest.directUnread ? <i className="jp-dot" aria-label="New" /> : null} onClick={() => goPlace({ kind: "direct" })} />
          <PhonePlace icon="fold" label="Files" onClick={() => goPlace({ kind: "files" })} />
        </section>
        {guest.rooms.map((r) => (
          <section className="gq-list" key={r.webinarId}>
            <button type="button" className="gq-room" aria-label={`${r.title}, its sessions`} onClick={() => openRoom(r.webinarId)}>
              {r.posterUrl ? <img src={r.posterUrl} alt="" /> : <span className="ph" aria-hidden="true" />}
              <span>
                <b>{r.title}</b>
                <small>{r.line}</small>
              </span>
            </button>
            {r.everyoneChat ? <PhonePlace icon="users" label="Everyone" end={r.everyoneNew ? <i className="jp-n">{r.everyoneNew}</i> : null} onClick={() => goPlace({ kind: "everyone", id: r.webinarId })} /> : null}
            <PhonePlace icon="bell" label="Announcements" end={r.announceNew ? <i className="jp-n">{r.announceNew}</i> : null} onClick={() => goPlace({ kind: "announce", id: r.webinarId })} />
            {r.circles.map((c) => (
              <PhonePlace
                key={c.circleId}
                icon="target"
                label={c.name}
                end={c.live ? <span className="jp-livetag">Live</span> : c.newCount ? <i className="jp-n">{c.newCount}</i> : null}
                onClick={() => goPlace({ kind: "circle", id: c.circleId })}
              />
            ))}
          </section>
        ))}
        <MadeWith />
      </div>
    </div>
  );
}

function PhoneMarkAll(): ReactNode {
  const { markAllSeen } = usePage();
  return (
    <button type="button" className="jp-link" onClick={markAllSeen}>
      Mark all as seen
    </button>
  );
}

function PhonePlace({ icon, face, label, sub, end, onClick }: { icon?: "fold" | "users" | "bell" | "target"; face?: boolean; label: string; sub?: string; end?: ReactNode; onClick: () => void }): ReactNode {
  const { host } = usePage();
  return (
    <button type="button" className="gq-row" onClick={onClick}>
      {face ? <Face name={host.name} photo={host.avatar} size={32} /> : icon ? <Icon name={icon} /> : null}
      <span className="gq-row-t">
        <b>{label}</b>
        {sub ? <small>{sub}</small> : null}
      </span>
      {end}
      <Icon name="chev" />
    </button>
  );
}

